import { GridViewIcon, PlusSignSquareIcon } from "@hugeicons-pro/core-duotone-rounded";
import { MoreHorizontalCircle01Icon, PodcastIcon } from "@hugeicons-pro/core-solid-rounded";
import {
	AddSquareIcon,
	ClockIcon,
	Delete03Icon,
	Mic01Icon as EpisodeIconDutotone,
	ListViewIcon,
	Calendar01Icon as PlanningIconDutotone,
	Settings05Icon as SettingsIconDutotone,
	AnalyticsUpIcon as StatisticsIconDutotone,
	UserCircleIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { type ComponentProps, useState } from "react";
import { AppContainer } from "~/components/layouts/app/app-container";
import { usePreferences } from "~/components/preferences-provider";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import {
	Card,
	CardAction,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "~/components/ui/card";
import { CardLoadingIndicator } from "~/components/ui/card-loading-indicator";
import { DebouncedInput } from "~/components/ui/debounced-input";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { PageHead } from "~/components/ui/page-head";
import { Toggle } from "~/components/ui/toggle";
import { ToggleGroup, ToggleGroupItem } from "~/components/ui/toggle-group";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "~/components/ui/tooltip";
import { formatDate } from "~/lib/date";
import { timeAgo } from "~/lib/time";
import { cn } from "~/lib/utils";
import {
	type Channel,
	ChannelStatus,
	type ChannelStatusValue,
	getThemeColor,
} from "~/models/channel";
import { useTRPC } from "~/trpc/client";

export const Route = createFileRoute("/_app/manager/")({
	component: Page,
	async loader({ context }) {
		context.queryClient.prefetchQuery(context.trpc.channel.list.queryOptions());
	},
});

function Page() {
	const { t } = useLingui();
	const trpc = useTRPC();
	const { preferences, setPreferences } = usePreferences();

	const [search, setSearch] = useState("");
	const [includeRemoved, setIncludeRemoved] = useState(false);

	const { data: channels, isFetching } = useQuery(
		trpc.channel.list.queryOptions(undefined, {
			select: (data) => {
				let channels = data;

				if (!includeRemoved) {
					channels = channels.filter(
						(channel) =>
							![ChannelStatus.Removed_TS, ChannelStatus.Removed].includes(channel.statusID),
					);
				}

				if (search.trim()) {
					channels = channels.filter((channel) =>
						channel.title.toLowerCase().includes(search.toLowerCase()),
					);
				}

				return channels.sort((a, b) => {
					const dateA = a.updatedDate ? new Date(a.updatedDate).getTime() : 0;
					const dateB = b.updatedDate ? new Date(b.updatedDate).getTime() : 0;
					return dateB - dateA;
				});
			},
		}),
	);

	return (
		<>
			<PageHead
				icon={PodcastIcon}
				title={t`Podcasts`}
				subtitle={t`Quick overview of all your shows.`}
				action={
					<Button asChild className="w-fit" variant="outline">
						<Link to="/manager/new">
							<HugeiconsIcon icon={AddSquareIcon} aria-hidden="true" className="size-4" />
							<Trans>Create or Import a Podcast</Trans>
						</Link>
					</Button>
				}
			/>
			<AppContainer className="my-12">
				<Card>
					<CardHeader>
						<CardTitle className="text-xl">
							<Trans>All your podcasts</Trans>
						</CardTitle>
						<CardDescription>
							<Trans>Quick overview of all your shows.</Trans>
						</CardDescription>
						<CardAction className="flex items-center gap-2">
							<DebouncedInput
								type="search"
								placeholder={t`Search podcasts...`}
								className="max-w-sm"
								value={search}
								onChange={(value) => setSearch(value.toString())}
							/>
							<ToggleGroup
								type="single"
								value={preferences.channelsListView}
								onValueChange={(value) => setPreferences({ channelsListView: value })}
								variant="outline"
							>
								<ToggleGroupItem value="grid" aria-label={t`Grid view`}>
									<HugeiconsIcon icon={GridViewIcon} />
								</ToggleGroupItem>
								<ToggleGroupItem value="list" aria-label={t`List view`}>
									<HugeiconsIcon icon={ListViewIcon} />
								</ToggleGroupItem>
							</ToggleGroup>
							<TooltipProvider>
								<Tooltip>
									<TooltipTrigger>
										<Toggle
											pressed={includeRemoved}
											onPressedChange={(pressed) => {
												setIncludeRemoved(pressed);
											}}
											variant="outline"
										>
											<HugeiconsIcon icon={Delete03Icon} />
										</Toggle>
									</TooltipTrigger>
									<TooltipContent>
										<Trans>Include removed podcasts</Trans>
									</TooltipContent>
								</Tooltip>
							</TooltipProvider>
						</CardAction>
					</CardHeader>
					<CardContent className="relative grid min-h-40 items-start justify-start gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
						{channels?.map((channel) =>
							preferences.channelsListView === "grid" ? (
								<ChannelCard key={channel.uid} channel={channel} />
							) : (
								<ChannelListItem key={channel.uid} channel={channel} />
							),
						)}
						{isFetching && <CardLoadingIndicator />}
						{!isFetching && (!channels || channels.length === 0) && (
							<div className="col-span-full flex h-full w-full flex-col items-center justify-center gap-4">
								<div className="bg-muted flex size-10 items-center justify-center rounded-md border">
									<HugeiconsIcon
										icon={PlusSignSquareIcon}
										className="text-muted-foreground size-6"
									/>
								</div>
								<h2 className="font-medium">
									<Trans>Create your first podcast</Trans>
								</h2>
								<p className="text-secondary-foreground text-sm">
									<Trans>
										It seems you don't have any podcasts yet. Do you want to create or import a new
										one?
									</Trans>
								</p>
								<Button asChild className="w-fit" size="lg">
									<Link to="/manager/new">
										<Trans>Create or Import a Podcast</Trans>
									</Link>
								</Button>
							</div>
						)}
					</CardContent>
				</Card>
			</AppContainer>
		</>
	);
}

function ChannelListItem({ channel }: { channel: Channel }) {
	const { i18n } = useLingui();
	const isAvailable = ![ChannelStatus.Removed_TS, ChannelStatus.Removed].includes(channel.statusID);

	return (
		<div
			className={cn(
				"group relative col-span-full flex flex-row items-center gap-4 rounded-md p-4",
				{
					"bg-accent/30 shadow-xs-with-border hover:bg-accent transition-colors": isAvailable,
					"bg-black/10": !isAvailable,
				},
			)}
		>
			<div className="size-18 shrink-0 overflow-hidden rounded-md ring-1 ring-black/[0.08] transition ring-inset dark:ring-white/[0.08]">
				<ChannelAvatar channel={channel} small />
			</div>
			<div className="flex flex-1 items-center gap-2">
				<div className="truncate">
					<h2 className="truncate leading-none font-medium hover:underline">{channel.title}</h2>
					<div className="flex items-center gap-2">
						<p className="text-muted-foreground mt-2 truncate text-sm">
							<HugeiconsIcon icon={UserCircleIcon} className="mr-1 mb-0.5 inline size-3" />
							{(channel.author ?? "").trim().length > 0 ? channel.author : "—"}
						</p>
						<TooltipProvider>
							<Tooltip>
								<TooltipTrigger>
									<p className="text-muted-foreground mt-2 text-sm">
										<HugeiconsIcon icon={ClockIcon} className="mr-1 mb-0.5 inline size-3" />
										<Trans>
											Last updated {channel.updatedDate ? timeAgo(channel.updatedDate) : "—"}
										</Trans>
									</p>
								</TooltipTrigger>
								<TooltipContent>
									{channel.updatedDate
										? formatDate(
												channel.updatedDate,
												{
													timeStyle: "short",
													dateStyle: "medium",
												},
												i18n.locale,
											)
										: "—"}
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					</div>
				</div>
				{isAvailable && (
					<DropdownMenu>
						<DropdownMenuTrigger asChild className="relative z-10 ml-auto shrink-0">
							<Button variant="ghost" size="icon">
								<span className="sr-only">
									<Trans>Open menu</Trans>
								</span>
								<HugeiconsIcon icon={MoreHorizontalCircle01Icon} />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem asChild>
								<Link
									to="/podcasts/$channelUid/episodes"
									params={{
										channelUid: channel.uid,
									}}
								>
									<HugeiconsIcon icon={EpisodeIconDutotone} />
									<Trans>Episodes</Trans>
								</Link>
							</DropdownMenuItem>
							<DropdownMenuItem asChild>
								<Link
									to="/podcasts/$channelUid/statistics"
									params={{
										channelUid: channel.uid,
									}}
								>
									<HugeiconsIcon icon={StatisticsIconDutotone} />
									<Trans>Statistics</Trans>
								</Link>
							</DropdownMenuItem>
							<DropdownMenuItem asChild>
								<Link
									to="/podcasts/$channelUid/statistics"
									params={{
										channelUid: channel.uid,
									}}
								>
									<HugeiconsIcon icon={PlanningIconDutotone} />
									<Trans>Planning</Trans>
								</Link>
							</DropdownMenuItem>
							<DropdownMenuItem asChild>
								<Link
									to="/podcasts/$channelUid/settings"
									params={{
										channelUid: channel.uid,
									}}
								>
									<HugeiconsIcon icon={SettingsIconDutotone} />
									<Trans>Settings</Trans>
								</Link>
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				)}
			</div>

			{isAvailable && (
				<Link
					to="/podcasts/$channelUid"
					params={{ channelUid: channel.uid }}
					className="absolute inset-0"
				/>
			)}
		</div>
	);
}

function ChannelCard({ channel }: { channel: Channel }) {
	const isAvailable = ![ChannelStatus.Removed_TS, ChannelStatus.Removed].includes(channel.statusID);

	return (
		<div
			className={cn("group relative rounded-md p-4", {
				"bg-accent/30 shadow-xs-with-border": isAvailable,
				"bg-black/10": !isAvailable,
			})}
		>
			<div className="size-full shrink-0 overflow-hidden rounded-md ring-1 ring-black/[0.08] transition ring-inset dark:ring-white/[0.08]">
				{isAvailable ? (
					<Link to="/podcasts/$channelUid" params={{ channelUid: channel.uid }}>
						<ChannelAvatar channel={channel} />
					</Link>
				) : (
					<ChannelAvatar channel={channel} />
				)}
			</div>
			<div className="mt-4 flex items-center gap-2">
				<div className="truncate">
					{isAvailable ? (
						<Link to="/podcasts/$channelUid" params={{ channelUid: channel.uid }}>
							<h2 className="truncate leading-none font-medium hover:underline">{channel.title}</h2>
						</Link>
					) : (
						<h2 className="truncate leading-none font-medium hover:underline">{channel.title}</h2>
					)}
					<p className="text-muted-foreground truncate text-sm">
						{(channel.author ?? "").trim().length > 0 ? channel.author : "—"}
					</p>
				</div>
				{isAvailable && (
					<DropdownMenu>
						<DropdownMenuTrigger asChild className="ml-auto shrink-0">
							<Button variant="ghost" size="icon">
								<span className="sr-only">
									<Trans>Open menu</Trans>
								</span>
								<HugeiconsIcon icon={MoreHorizontalCircle01Icon} />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem asChild>
								<Link
									to="/podcasts/$channelUid/episodes"
									params={{
										channelUid: channel.uid,
									}}
								>
									<HugeiconsIcon icon={EpisodeIconDutotone} />
									<Trans>Episodes</Trans>
								</Link>
							</DropdownMenuItem>
							<DropdownMenuItem asChild>
								<Link
									to="/podcasts/$channelUid/statistics"
									params={{
										channelUid: channel.uid,
									}}
								>
									<HugeiconsIcon icon={StatisticsIconDutotone} />
									<Trans>Statistics</Trans>
								</Link>
							</DropdownMenuItem>
							<DropdownMenuItem asChild>
								<Link
									to="/podcasts/$channelUid/statistics"
									params={{
										channelUid: channel.uid,
									}}
								>
									<HugeiconsIcon icon={PlanningIconDutotone} />
									<Trans>Planning</Trans>
								</Link>
							</DropdownMenuItem>
							<DropdownMenuItem asChild>
								<Link
									to="/podcasts/$channelUid/settings"
									params={{
										channelUid: channel.uid,
									}}
								>
									<HugeiconsIcon icon={SettingsIconDutotone} />
									<Trans>Settings</Trans>
								</Link>
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				)}
			</div>
			<ChannelStatusIndicator statusID={channel.statusID} className="absolute top-1.5 right-1.5" />
		</div>
	);
}

function ChannelAvatar({ channel, small }: { channel: Channel; small?: boolean }) {
	return (
		<Avatar className="size-full rounded">
			<AvatarImage src={channel.iconsAssetURL} alt={channel.title} />
			<AvatarFallback
				color={getThemeColor(channel.themeColor)}
				className={cn("aspect-square size-full rounded", {
					"text-7xl": !small,
					"text-4xl": small,
				})}
			>
				{channel.title.slice(0, 2)}
			</AvatarFallback>
		</Avatar>
	);
}

function ChannelStatusIndicator({
	className,
	statusID,
	...props
}: ComponentProps<"div"> & {
	statusID: ChannelStatusValue;
}) {
	switch (true) {
		case [ChannelStatus.Removed_TS, ChannelStatus.Removed].includes(statusID):
			return (
				<Tooltip>
					<TooltipTrigger asChild>
						<div
							className={cn("animate-pulse rounded-full bg-red-500/20 p-2", className)}
							{...props}
						>
							<div className="size-3 rounded-full bg-red-500" />
						</div>
					</TooltipTrigger>
					<TooltipContent>
						<p className="text-muted-foreground text-xs font-semibold">
							<Trans>Removed</Trans>
						</p>
					</TooltipContent>
				</Tooltip>
			);
		case [ChannelStatus.Active, ChannelStatus.Active_B2B].includes(statusID):
			return (
				<Tooltip>
					<TooltipTrigger asChild>
						<div
							className={cn("animate-pulse rounded-full bg-emerald-500/20 p-2", className)}
							{...props}
						>
							<div className="size-3 rounded-full bg-emerald-500" />
						</div>
					</TooltipTrigger>
					<TooltipContent>
						<p className="text-muted-foreground text-xs font-semibold">
							<Trans>Active</Trans>
						</p>
					</TooltipContent>
				</Tooltip>
			);
		case [ChannelStatus.Frozen].includes(statusID):
			return (
				<Tooltip>
					<TooltipTrigger asChild>
						<div className={cn("rounded-full bg-gray-500/20 p-2", className)} {...props}>
							<div className="size-3 rounded-full bg-gray-500" />
						</div>
					</TooltipTrigger>
					<TooltipContent>
						<p className="text-muted-foreground text-xs font-semibold">
							<Trans>Frozen</Trans>
						</p>
					</TooltipContent>
				</Tooltip>
			);
		case [ChannelStatus.Incomplete].includes(statusID):
			return (
				<Tooltip>
					<TooltipTrigger asChild>
						<div className={cn("rounded-full bg-amber-500/20 p-2", className)} {...props}>
							<div className="size-3 rounded-full bg-amber-500" />
						</div>
					</TooltipTrigger>
					<TooltipContent>
						<p className="text-muted-foreground text-xs font-semibold">
							<Trans>Incomplete</Trans>
						</p>
					</TooltipContent>
				</Tooltip>
			);
		case [ChannelStatus.Rss_Syncing].includes(statusID):
			return (
				<Tooltip>
					<TooltipTrigger asChild>
						<div
							className={cn("animate-pulse rounded-full bg-orange-500/20 p-2", className)}
							{...props}
						>
							<div className="size-3 rounded-full bg-orange-500" />
						</div>
					</TooltipTrigger>
					<TooltipContent>
						<p className="text-muted-foreground text-xs font-semibold">
							<Trans>RSS Syncing</Trans>
						</p>
					</TooltipContent>
				</Tooltip>
			);
		case [ChannelStatus.ReTranscode].includes(statusID):
			return (
				<Tooltip>
					<TooltipTrigger asChild>
						<div
							className={cn("animate-pulse rounded-full bg-indigo-500/20 p-2", className)}
							{...props}
						>
							<div className="size-3 rounded-full bg-indigo-500" />
						</div>
					</TooltipTrigger>
					<TooltipContent>
						<p className="text-muted-foreground text-xs font-semibold">
							<Trans>Processing</Trans>
						</p>
					</TooltipContent>
				</Tooltip>
			);
		default:
			return null;
	}
}
